import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertiesService, rentalsService } from '../services/api';
import { AppNav } from '../components/AppNav';

interface PropertyOpt { id: string; title: string }
interface UnitOpt { id: string; name: string; status: string }
interface TenantOpt { id: string; name: string }

export default function LeaseCreatePage() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<PropertyOpt[]>([]);
  const [propertyId, setPropertyId] = useState('');
  const [units, setUnits] = useState<UnitOpt[]>([]);
  const [unitId, setUnitId] = useState('');
  const [newUnitName, setNewUnitName] = useState('');
  const [tenants, setTenants] = useState<TenantOpt[]>([]);
  const [tenantId, setTenantId] = useState('');
  const [newTenant, setNewTenant] = useState({ name: '', whatsapp: '', curp: '' });
  const [terms, setTerms] = useState({
    start_date: '',
    end_date: '',
    monthly_rent: '',
    rent_day: '1',
    deposit: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    propertiesService
      .search({})
      .then((r: { items: PropertyOpt[] }) => setProperties(r.items))
      .catch(() => {});
    rentalsService.listTenants<TenantOpt>().then((r) => setTenants(r.items)).catch(() => {});
  }, []);

  useEffect(() => {
    setUnitId('');
    setUnits([]);
    if (!propertyId) return;
    rentalsService.listUnits(propertyId).then(setUnits).catch(() => {});
  }, [propertyId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      let finalUnitId = unitId;
      if (!finalUnitId && newUnitName && propertyId) {
        const unit = await rentalsService.createUnit(propertyId, { name: newUnitName });
        finalUnitId = unit.id;
        setUnitId(unit.id);
        setNewUnitName('');
      }
      let finalTenantId = tenantId;
      if (!finalTenantId && newTenant.name) {
        const tenant = await rentalsService.createTenant({
          name: newTenant.name,
          whatsapp: newTenant.whatsapp || null,
          curp: newTenant.curp || null,
        });
        finalTenantId = tenant.id;
        setTenantId(tenant.id);
        setNewTenant({ name: '', whatsapp: '', curp: '' });
      }
      if (!finalUnitId || !finalTenantId) {
        setError('Selecciona o crea unidad e inquilino.');
        return;
      }
      const lease = await rentalsService.createLease({
        unit_id: finalUnitId,
        tenant_id: finalTenantId,
        start_date: terms.start_date,
        end_date: terms.end_date,
        monthly_rent: Number(terms.monthly_rent),
        rent_day: terms.rent_day ? Number(terms.rent_day) : 1,
        deposit: terms.deposit ? Number(terms.deposit) : null,
      });
      navigate(`/admin/rentals/leases/${lease.id}`);
    } catch (err) {
      // QA 2026-10-23: decir la causa accionable en vez de un genérico
      const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
      const detail = typeof res?.data?.detail === 'string' ? res.data.detail : '';
      if (res?.status === 403) {
        setError('Verifica tu correo para crear arrendamientos.');
      } else if (detail.includes('active lease')) {
        setError('Esa unidad ya tiene un arrendamiento activo.');
      } else if (detail.includes('end_date')) {
        setError('La fecha de fin debe ser posterior a la de inicio.');
      } else if (detail.includes('whatsapp')) {
        setError('El WhatsApp del inquilino no es un número válido.');
      } else {
        setError('No se pudo crear el arrendamiento.');
      }
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full px-3 py-2 text-sm bg-white border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring';

  return (
    <div className="min-h-[100dvh] bg-background">
      <AppNav />
      <main className="max-w-2xl mx-auto px-6 py-8">
        <h1 className="text-2xl font-semibold tracking-tight mb-6">Nuevo arrendamiento</h1>
        <form onSubmit={submit} className="space-y-8">
          <section>
            <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-3">
              1. Unidad
            </h2>
            <select
              aria-label="Propiedad"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              className={inputCls}
            >
              <option value="">Selecciona propiedad…</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
            <select
              aria-label="Unidad"
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              className={`${inputCls} mt-2`}
            >
              <option value="">Selecciona unidad…</option>
              {units.filter((u) => u.status === 'available').map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <input
              placeholder="…o crea una nueva unidad (nombre)"
              value={newUnitName}
              onChange={(e) => setNewUnitName(e.target.value)}
              className={`${inputCls} mt-2`}
            />
          </section>

          <section>
            <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-3">
              2. Inquilino
            </h2>
            <select
              aria-label="Inquilino"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className={inputCls}
            >
              <option value="">Selecciona inquilino…</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
              <input placeholder="…o nombre nuevo" value={newTenant.name}
                onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })} className={inputCls} />
              <input placeholder="WhatsApp +521…" value={newTenant.whatsapp}
                onChange={(e) => setNewTenant({ ...newTenant, whatsapp: e.target.value })} className={inputCls} />
              <input placeholder="CURP (opcional)" value={newTenant.curp}
                onChange={(e) => setNewTenant({ ...newTenant, curp: e.target.value })} className={inputCls} />
            </div>
          </section>

          <section>
            <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-3">
              3. Términos
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="text-xs text-muted-foreground">
                Inicio
                <input type="date" required value={terms.start_date}
                  onChange={(e) => setTerms({ ...terms, start_date: e.target.value })} className={inputCls} />
              </label>
              <label className="text-xs text-muted-foreground">
                Fin
                <input type="date" required value={terms.end_date}
                  onChange={(e) => setTerms({ ...terms, end_date: e.target.value })} className={inputCls} />
              </label>
              <label className="text-xs text-muted-foreground">
                Renta mensual (MXN)
                <input type="number" required min="1" value={terms.monthly_rent}
                  onChange={(e) => setTerms({ ...terms, monthly_rent: e.target.value })} className={inputCls} />
              </label>
              <label className="text-xs text-muted-foreground">
                Día de pago
                <input type="number" required min="1" max="31" value={terms.rent_day}
                  onChange={(e) => setTerms({ ...terms, rent_day: e.target.value })} className={inputCls} />
              </label>
              <label className="text-xs text-muted-foreground">
                Depósito (opcional)
                <input type="number" value={terms.deposit}
                  onChange={(e) => setTerms({ ...terms, deposit: e.target.value })} className={inputCls} />
              </label>
            </div>
          </section>

          {error && <p className="text-sm text-destructive">{error}</p>}
          <button
            type="submit"
            disabled={saving}
            className="w-full bg-primary text-primary-foreground py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Creando…' : 'Crear arrendamiento'}
          </button>
        </form>
      </main>
    </div>
  );
}
